import { Injectable } from "@angular/core";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Observable, Subject } from "rxjs";
import { catchError, map, takeUntil } from "rxjs/operators";
import moment from "moment";
import { MyKadData } from "../regmodels/MmyKadData";
import { ThaiCardData } from "../regmodels/MThaiCardData";
import { CARD_READER_TYPE } from "../constants/card-reader-constants";
import { STATICCODESERVICE } from "../constants/static-code-service";
import { EnvoirnmentService } from "./envoirnment.service";

declare const Sekure: any;

/**
 * Centralised card-reader service.
 *
 * Manages WebSocket and Tricubes SDK card-reader connections, adapts each
 * vendor's raw response into a normalised {@link MyKadData} object, and
 * broadcasts the result via {@link cardData$}.
 *
 * Usage in a registration component:
 * ```typescript
 * // ngOnInit / initMyKadByConfig
 * this.cardReaderService.initialize(this.env.cardReaderType);
 * this.cardReaderService.cardData$
 *   .pipe(takeUntil(this._onDestroy))
 *   .subscribe(data => this.loadMyKadData(data));
 *
 * // ngOnDestroy
 * this.cardReaderService.destroy();
 * ```
 *
 * Adding a new card type:
 * 1. Add a constant to {@link CARD_READER_TYPE}.
 * 2. Create a raw-data model (e.g. `MSingaporeCardData.ts`).
 * 3. Add a private `adaptXxxResponse()` method in this service.
 * 4. Register the new case in {@link initialize} (and in
 *    {@link adaptWebSocketMessage} if WebSocket-based).
 */
@Injectable({
  providedIn: "root",
})
export class CardReaderService {
  private _cardDataSubject = new Subject<MyKadData>();
  private _sockets: WebSocket[] = [];
  private _statusCheckInterval: any = null;
  private _isFormActive = true;

  /** Emits normalised card data whenever a card is successfully read. */
  cardData$ = this._cardDataSubject.asObservable();

  /**
   * Used to cancel in-flight HTTP requests (Tricubes SDK) when the
   * component that owns the current reader session is destroyed.
   */
  private _destroy$ = new Subject<boolean>();

  constructor(
    private httpClient: HttpClient,
    private snackBar: MatSnackBar,
    private env: EnvoirnmentService
  ) {}

  // ─── Lifecycle ──────────────────────────────────────────────────────────────

  /**
   * Initialise the appropriate card reader for the given type.
   * Tears down any previous connection first so multiple components can
   * call this safely in sequence.
   *
   * @param cardReaderType One of the {@link CARD_READER_TYPE} constants.
   */
  initialize(cardReaderType: string): void {
    this.destroy(); // clean up any previous session

    switch (cardReaderType) {
      case CARD_READER_TYPE.TRICUBES_SDK:
        this.initTricubesSDK();
        break;
      case CARD_READER_TYPE.WEBSOCKET:
        this.initWebSocket("ws://localhost:3000");
        this.initWebSocket("ws://localhost:3001");
        break;
      default:
        break;
    }
  }

  /**
   * Control whether TRICUBES_SDK polling should fire.
   * Components can pass their `patientForm` flag here when it changes.
   */
  setFormActive(active: boolean): void {
    this._isFormActive = active;
  }

  /**
   * Manually trigger a card read – intended for TRICUBES_SDK button clicks.
   * No-op for WebSocket mode (reads arrive automatically).
   */
  triggerRead(): void {
    this.checkStatus();
  }

  /**
   * Tear down connections and cancel pending requests.
   * Call from the component's `ngOnDestroy`.
   */
  destroy(): void {
    this._destroy$.next(true); // cancel any pending HTTP subscriptions

    this._sockets.forEach((s) => s.close());
    this._sockets = [];

    if (this._statusCheckInterval) {
      clearInterval(this._statusCheckInterval);
      this._statusCheckInterval = null;
    }
  }

  // ─── Tricubes SDK ────────────────────────────────────────────────────────────

  private initTricubesSDK(): void {
    const jqueryScript = document.createElement("script");
    jqueryScript.src = "https://code.jquery.com/jquery-3.6.0.min.js";
    jqueryScript.onload = () => {
      console.log("jQuery Loaded Successfully");
      const sekureScript = document.createElement("script");
      sekureScript.src = "https://localhost:9996/api/files/Sekure.js";
      sekureScript.onload = () => {
        console.log("Sekure Manager Loaded Successfully");
        if (typeof Sekure === "undefined") {
          console.log("Sekure Manager is not Running.");
        }
        // Automatic polling can be enabled here:
        // this.startStatusCheckInterval();
      };
      sekureScript.onerror = () => console.log("Failed to Load Sekure Library.");
      document.body.appendChild(sekureScript);
    };
    jqueryScript.onerror = () => console.log("Failed to Load jQuery.");
    document.body.appendChild(jqueryScript);
  }

  /** Start a 10-second polling loop (optional – currently left to manual trigger). */
  startStatusCheckInterval(): void {
    this._statusCheckInterval = setInterval(() => {
      if (this._isFormActive) this.checkStatus();
    }, 10000);
  }

  /** Query the Sekure SDK for card presence; on success, reads card data. */
  checkStatus(): void {
    if (!this._isFormActive) return;

    this.getSekureApiData("CheckStatus/CheckStatusCallback")
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (response) => {
          if (response?.Data?.CardStatus === 0) {
            this.getJpnInfo();
          }
        },
        (error: HttpErrorResponse) => {
          console.error("Error checking card status:", error);
          this.snackBar.open("ERROR", "Please Check Card Reader SDK", { duration: 2500 });
        }
      );
  }

  /** Fetch card info from the Sekure SDK and adapt to {@link MyKadData}. */
  getJpnInfo(): void {
    this.getSekureApiData("GetJpnInfo/GetJpnInfoCallback")
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (response) => {
          if (response?.Data) {
            const data = response.Data;
            if (data.ResponseCode === "READING_ERROR" || data.szOName === "") {
              this.snackBar.open(
                "ERROR",
                "Error Reading Card, Please Restart the Sekure Manager",
                { duration: 3000 }
              );
            } else {
              const myKadData = this.adaptMyKadResponse(data);
              this.getJpnPhoto(myKadData);
            }
          }
        },
        (error: HttpErrorResponse) => {
          console.error("Error reading card info:", error);
          this.snackBar.open("ERROR", "Please Check Card Reader SDK", { duration: 2500 });
        }
      );
  }

  private getJpnPhoto(myKadData: MyKadData): void {
    this.getSekureApiData("GetJpnPhoto/GetJpnPhotoCallback")
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (response) => {
          if (response?.Data) {
            if (response.Data.ResponseCode === "READING_ERROR") {
              this.snackBar.open(
                "ERROR",
                "Error Reading Card, Please Restart the Sekure Manager",
                { duration: 3000 }
              );
            } else {
              myKadData.PhotoBase64 = response.Data.imgPhoto;
              this._cardDataSubject.next(myKadData);
            }
          } else {
            this._cardDataSubject.next(myKadData);
          }
        },
        (error: HttpErrorResponse) => {
          console.error("Error reading card photo:", error);
          this.snackBar.open("ERROR", "Please Check Card Reader SDK", { duration: 2500 });
        }
      );
  }

  private getSekureApiData(url: string): Observable<any> {
    return this.httpClient
      .get("https://localhost:9996/api/" + url, { responseType: "text" })
      .pipe(
        map((response) => this.extractJsonFromResponse(response)),
        catchError((error: HttpErrorResponse) => {
          console.error("Sekure API Error:", error);
          this.snackBar.open("ERROR", "Please Check Card Reader SDK", { duration: 2500 });
          return [];
        })
      );
  }

  private extractJsonFromResponse(response: string): any {
    const postMessageMatch = response.match(/postMessage\('(.*)'\s*,\s*'\*'\)/);
    if (postMessageMatch?.[1]) {
      try {
        return JSON.parse(postMessageMatch[1]);
      } catch (e) {
        console.error("Error parsing Sekure JSON response:", e);
        this.snackBar.open("ERROR", "Please Check Card Reader SDK", { duration: 2500 });
        return null;
      }
    }
    return null;
  }

  // ─── WebSocket ───────────────────────────────────────────────────────────────

  private initWebSocket(url: string): void {
    try {
      const socket = new WebSocket(url);
      this._sockets.push(socket);

      socket.onopen = () =>
        console.log(`Card reader WebSocket connection established: ${url}`);

      socket.onmessage = (event) => {
        console.log("Card reader WebSocket message received:", event.data);
        const myKadData = this.adaptWebSocketMessage(event.data);
        if (myKadData) this._cardDataSubject.next(myKadData);
      };

      socket.onclose = (event) =>
        console.log(`Card reader WebSocket connection closed: ${url}`, event);

      socket.onerror = (error) =>
        console.error(`Card reader WebSocket error: ${url}`, error);
    } catch (e) {
      console.error("Failed to initialise card reader WebSocket:", e);
    }
  }

  // ─── Adapters ────────────────────────────────────────────────────────────────

  /**
   * Parse a raw WebSocket message and route it to the correct adapter.
   *
   * Detection strategy:
   * - Presence of `p_cid` → Thai national ID card ({@link adaptThaiCardResponse}).
   * - Otherwise → fall back to treating the payload as a standard
   *   {@link MyKadData}-shaped object (e.g. direct JSON serialisation of MyKad data).
   *
   * To support a new card type, add a detection condition here and a
   * corresponding private `adaptXxxResponse()` method.
   */
  private adaptWebSocketMessage(rawMessage: string): MyKadData | null {
    try {
      const parsed =
        typeof rawMessage === "string" ? JSON.parse(rawMessage) : rawMessage;

      if (parsed.p_cid !== undefined) {
        // Thai national ID card format
        return this.adaptThaiCardResponse(parsed as ThaiCardData);
      }

      // Default: assume the payload is already MyKadData-shaped
      return parsed as MyKadData;
    } catch (e) {
      console.error("Error parsing card reader WebSocket message:", e);
      return null;
    }
  }

  /**
   * Map a Tricubes Sekure SDK response to normalised {@link MyKadData}.
   */
  private adaptMyKadResponse(data: any): MyKadData {
    const myKadData = new MyKadData();
    myKadData.Name = data.szOName || "";
    myKadData.ICNo = data.szIDNo || "";
    myKadData.BirthDate = data.szBirthDate || "";
    myKadData.Gender = data.szGender
      ? data.szGender === "L"
        ? STATICCODESERVICE.GENDER_MASTER_MALE
        : STATICCODESERVICE.GENDER_MASTER_FEMALE
      : "";
    myKadData.Race = data.szRace || "";
    myKadData.Religion = data.szRelegion || "";
    myKadData.Address1 = data.szAddress1 || "";
    myKadData.Address2 = data.szAddress2 || "";
    myKadData.Address3 = data.szAddress3 || "";
    myKadData.City = data.szCity || "";
    myKadData.State = data.szState || "";
    myKadData.Postcode = data.szPostCode || "";
    myKadData.BirthPlace = data.szBirthPlace || "";
    myKadData.OldICNo = data.szOldIdNo || "";
    myKadData.CardType = STATICCODESERVICE.IDENTIFICATION_TYPE_MYKAD;
    return myKadData;
  }

  /**
   * Map a Thai national ID card response to normalised {@link MyKadData}.
   *
   * Normalisation decisions:
   * - `Name` is set to "{FirstNameEn} {LastNameEn}" so that the standard
   *   `if (myKadData.Name)` sentinel in each component's `loadMyKadData`
   *   continues to work without modification.
   * - Dates are converted from ISO 8601 to "DD/MM/YYYY" to match the format
   *   that `loadMyKadData` passes to moment.js for parsing.
   * - `Gender` is translated from "1"/"2" to the system codes used across
   *   the application ("MALE"/"FEMALE").
   */
  private adaptThaiCardResponse(raw: ThaiCardData): MyKadData {
    const myKadData = new MyKadData();

    // Full display name (sentinel) + split names for Thai form fields
    myKadData.Name = [raw.p_firstname_en, raw.p_lastname_en]
      .filter(Boolean)
      .join(" ");
    myKadData.FirstNameEn = raw.p_firstname_en || "";
    myKadData.LastNameEn = raw.p_lastname_en || "";
    myKadData.FirstNameTh = raw.p_firstname_th || "";
    myKadData.LastNameTh = raw.p_lastname_th || "";
    myKadData.PrefixEn = raw.p_prefix_en || "";
    myKadData.PrefixTh = raw.p_prefix_th || "";

    myKadData.ICNo = raw.p_cid || "";

    // Card type: "1" → THAIID, "2" → PASSPORT
    myKadData.CardType = raw.p_doctype == "1"
      ? STATICCODESERVICE.IDENTIFICATION_TYPE_THAIID
      : STATICCODESERVICE.IDENTIFICATION_TYPE_PASSPORT;

    // Normalise dates to "DD/MM/YYYY" to match the format expected by loadMyKadData
    myKadData.BirthDate = raw.p_birthday
      ? moment(raw.p_birthday).format("DD/MM/YYYY")
      : "";
    myKadData.ExpiryDate = raw.p_expire
      ? moment(raw.p_expire).format("DD/MM/YYYY")
      : "";

    // Gender: "1" → MALE, "2" → FEMALE
    myKadData.Gender =
      raw.p_gender === "1"
        ? STATICCODESERVICE.GENDER_MASTER_MALE
        : raw.p_gender === "2"
        ? STATICCODESERVICE.GENDER_MASTER_FEMALE
        : "";

    // Combine address parts; village number is stored in Address2 when present
    myKadData.Address1 = [raw.p_addrHouseNo, raw.p_addrLane, raw.p_addrRoad]
      .filter(Boolean)
      .join(" ");
    myKadData.Address2 = raw.p_addrVillageNo || "";

    myKadData.SubDistrict = raw.p_addrTambol || "";
    myKadData.City = raw.p_addrAmphur || "";
    myKadData.State = raw.p_addrProvince || "";

    myKadData.PhotoBase64 = raw.p_photo || "";

    return myKadData;
  }
}
